import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';

import { useScrollFade } from '../scroll-fade/ScrollFade';

const HORIZONTAL_SCROLL_STEP_PX = 280;

type UseTableHorizontalScrollOptions = {
    readonly disabled?: boolean;
};

type UseTableHorizontalScrollResult = ReturnType<typeof useScrollFade> & {
    readonly isOverflowing: boolean;
    // Returns true when the event scrolled the viewport so callers can skip their own handling.
    readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => boolean;
};

// Horizontal overflow for grid-based tables: the horizontal scroll fade plus arrow-key scrolling
// for the focused viewport itself (the scrollbar is hidden by convention). Shared with grids that
// manage their own scroll container.
export const useTableHorizontalScroll = ({
    disabled = false,
}: UseTableHorizontalScrollOptions = {}): UseTableHorizontalScrollResult => {
    const scrollFade = useScrollFade({ disabled, orientation: 'horizontal' });
    const { overflowEdges } = scrollFade;
    const isOverflowing = !disabled && (overflowEdges.start || overflowEdges.end);
    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>): boolean => {
            if (!isOverflowing) return false;
            if (event.target !== event.currentTarget) return false;
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return false;

            event.preventDefault();
            event.currentTarget.scrollBy({
                left: event.key === 'ArrowLeft' ? -HORIZONTAL_SCROLL_STEP_PX : HORIZONTAL_SCROLL_STEP_PX,
            });

            return true;
        },
        [isOverflowing],
    );

    return { ...scrollFade, isOverflowing, onKeyDown };
};
